// Frontend/src/hooks/useUnits.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listUnits,
  listUnitsByBuilding,
  getUnit,
  createUnit,
  updateUnit,
  deleteUnit,
  importUnitsCSV,
  downloadUnitCSVTemplate,
  type UnitList,
  type Unit,
  type CreateUnitData,
  type UpdateUnitData,
  type UnitCSVImportResult,
} from "@/api/units";

/**
 * Query key factory for units
 */
export const unitKeys = {
  all: ["units"] as const,
  lists: () => [...unitKeys.all, "list"] as const,
  list: (buildingId?: string) => buildingId 
    ? [...unitKeys.lists(), { building: buildingId }] as const
    : [...unitKeys.lists()] as const,
  details: () => [...unitKeys.all, "detail"] as const,
  detail: (id: string) => [...unitKeys.details(), id] as const,
};

/**
 * Fetch all units (no building filter)
 */
export function useUnits() {
  return useQuery<UnitList[], Error>({
    queryKey: unitKeys.list(),
    queryFn: () => listUnits(),
  });
}

/**
 * Fetch units for a specific building
 */
export function useUnitsByBuilding(buildingId: string) {
  return useQuery<UnitList[], Error>({
    queryKey: unitKeys.list(buildingId),
    queryFn: () => listUnitsByBuilding(buildingId),
    enabled: !!buildingId,
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Fetch a single unit by ID
 */
export function useUnit(id: string) {
  return useQuery<Unit, Error>({
    queryKey: unitKeys.detail(id),
    queryFn: () => getUnit(id),
    enabled: !!id,
  });
}

/**
 * Create a new unit
 */
export function useCreateUnit() {
  const queryClient = useQueryClient();

  return useMutation<Unit, Error, CreateUnitData>({
    mutationFn: createUnit,
    onSuccess: () => {
      // Invalidate and refetch units list
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
      // Invalidate all building stats (since unit count changed)
      queryClient.invalidateQueries({ queryKey: ["buildings", "stats"] });
    },
  });
}

/**
 * Update an existing unit
 */
export function useUpdateUnit() {
  const queryClient = useQueryClient();

  return useMutation<Unit, Error, { id: string; data: UpdateUnitData }>({
    mutationFn: ({ id, data }) => updateUnit(id, data),
    onSuccess: (data) => {
      // Update the cache for this specific unit
      queryClient.setQueryData(unitKeys.detail(data.id), data);
      // Invalidate the list to refetch
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
    },
  });
}

/**
 * Delete a unit
 */
export function useDeleteUnit() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteUnit,
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: unitKeys.detail(deletedId) });
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
    },
  });
}

/**
 * Import units from CSV
 */
export function useImportUnitsCSV() {
  const queryClient = useQueryClient();

  return useMutation<UnitCSVImportResult, Error, File>({
    mutationFn: importUnitsCSV,
    onSuccess: () => {
      // Refetch all units after import
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
    },
  });
}

/**
 * Download CSV template
 */
export function useDownloadUnitCSVTemplate() {
  return useMutation<string, Error>({
    mutationFn: downloadUnitCSVTemplate,
  });
}
